(() => {
  if (window.INGENIO_CART_CROSS_SELL_LOADED) return;
  window.INGENIO_CART_CROSS_SELL_LOADED = true;

  const STORAGE_KEY = 'ingenio_cross_sell_v2';
  const PENDING_ADD_KEY = 'ingenio_cross_sell_pending_add_v2';
  const MAX_AGE_MS = 24 * 60 * 60 * 1000;
  const PENDING_MAX_AGE_MS = 2 * 60 * 1000;
  const VALID_FAMILIES = new Set(['mademap', 'puzzle']);
  const sentEvents = new Set();

  const now = () => Date.now();

  function analytics(eventName, parameters = {}) {
    const signature = `${eventName}:${parameters.family || ''}:${parameters.mode || ''}:${parameters.product_handle || ''}:${parameters.variant_id || ''}`;
    if (eventName === 'cart_cross_sell_impression' && sentEvents.has(signature)) return;
    if (eventName === 'cart_cross_sell_impression') sentEvents.add(signature);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...parameters });
  }

  function blankState(token = '') {
    return {
      cart_token: token,
      seen: { mademap: false, puzzle: false },
      impressed: { mademap: false, puzzle: false },
      last_family: '',
      updated_at: now(),
    };
  }

  function readState(token) {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      const expired = !stored?.updated_at || now() - Number(stored.updated_at) > MAX_AGE_MS;
      if (!stored || expired || stored.cart_token !== token) return blankState(token);
      return {
        ...blankState(token),
        ...stored,
        seen: { ...blankState(token).seen, ...(stored.seen || {}) },
        impressed: { ...blankState(token).impressed, ...(stored.impressed || {}) },
      };
    } catch (error) {
      console.warn('INGENIO cross-sell: no fue posible leer la memoria.', error);
      return blankState(token);
    }
  }

  function writeState(state) {
    const next = { ...state, updated_at: now() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      console.warn('INGENIO cross-sell: no fue posible guardar la memoria.', error);
    }
    return next;
  }

  function clearState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('INGENIO cross-sell: no fue posible reiniciar la memoria.', error);
    }
  }

  function readPendingAdd() {
    try {
      const pending = JSON.parse(sessionStorage.getItem(PENDING_ADD_KEY) || 'null');
      if (!pending?.family || now() - Number(pending.created_at || 0) > PENDING_MAX_AGE_MS) {
        sessionStorage.removeItem(PENDING_ADD_KEY);
        return null;
      }
      return pending;
    } catch (error) {
      sessionStorage.removeItem(PENDING_ADD_KEY);
      return null;
    }
  }

  function writePendingAdd(family, itemCount) {
    try {
      sessionStorage.setItem(
        PENDING_ADD_KEY,
        JSON.stringify({ family, item_count: Number(itemCount || 0), created_at: now() }),
      );
    } catch (error) {
      console.warn('INGENIO cross-sell: no fue posible registrar la adición pendiente.', error);
    }
  }

  function clearPendingAdd() {
    try {
      sessionStorage.removeItem(PENDING_ADD_KEY);
    } catch (error) {
      console.warn('INGENIO cross-sell: no fue posible limpiar la adición pendiente.', error);
    }
  }

  if (!customElements.get('cart-cross-sell')) {
    customElements.define(
      'cart-cross-sell',
      class CartCrossSell extends HTMLElement {
        connectedCallback() {
          this.token = this.dataset.cartToken || '';
          this.surface = this.dataset.surface || 'drawer';
          this.state = readState(this.token);
          this.bindControls();
          this.showCompact(this.preferredFamily());
          this.restorePendingAdd();
        }

        restorePendingAdd() {
          const cartPath = window.routes?.cart_url || '/cart';
          const isStaticCart = window.location.pathname === cartPath;
          if (isStaticCart && this.surface !== 'page') return;

          const pending = readPendingAdd();
          if (!pending || !VALID_FAMILIES.has(pending.family)) return;

          const currentItemCount = Number(this.dataset.itemCount || 0);
          const hasRelatedOffer = Boolean(
            this.querySelector(`[data-cross-sell-group="${pending.family}"]`),
          );
          clearPendingAdd();

          if (!hasRelatedOffer || currentItemCount <= Number(pending.item_count || 0)) return;
          const didExpand = this.handleEligibleAdd({ family: pending.family });
          if (!didExpand) return;

          requestAnimationFrame(() => {
            const drawer = this.closest('cart-drawer');
            if (drawer?.open) drawer.open();
          });
        }

        bindControls() {
          this.querySelectorAll('[data-cross-sell-expand]').forEach((button) => {
            button.addEventListener('click', () => {
              const family = button.dataset.crossSellExpand;
              this.expand(family, 'manual');
            });
          });

          this.querySelectorAll('[data-cross-sell-dismiss]').forEach((button) => {
            button.addEventListener('click', () => {
              const family = button.closest('[data-cross-sell-group]')?.dataset.crossSellGroup;
              analytics('cart_cross_sell_dismiss', this.eventData(family, 'full'));
              this.showCompact(family);
            });
          });

          this.querySelectorAll('[data-cross-sell-variant-select]').forEach((select) => {
            select.addEventListener('change', () => {
              const item = select.closest('[data-cross-sell-item]');
              const hiddenInput = item?.querySelector('[data-cross-sell-variant]');
              const addButton = item?.querySelector('[data-cross-sell-add]');
              const price = item?.querySelector('[data-cross-sell-price]');
              const option = select.selectedOptions[0];
              if (hiddenInput) hiddenInput.value = select.value;
              if (price) price.textContent = option?.dataset.price || 'Selecciona una opción';
              if (addButton) {
                addButton.disabled = !select.value;
                addButton.setAttribute('aria-disabled', String(!select.value));
              }
            });
          });

          this.querySelectorAll('[data-cross-sell-add]').forEach((button) => {
            button.addEventListener('click', () => this.addAccessory(button));
          });
        }

        availableFamilies() {
          return [...this.querySelectorAll('[data-cross-sell-group]')].map(
            (group) => group.dataset.crossSellGroup,
          );
        }

        preferredFamily() {
          const families = this.availableFamilies();
          return families.includes(this.state.last_family) ? this.state.last_family : families[0];
        }

        eventData(family, mode, extra = {}) {
          return {
            family,
            mode,
            cart_token: this.token,
            cart_value: Number(this.dataset.cartValue || 0) / 100,
            eligible_count: Number(this.dataset[`${family}Count`] || 0),
            ...extra,
          };
        }

        showCompact(preferredFamily = '') {
          this.querySelectorAll('[data-cross-sell-group]').forEach((group) => {
            const family = group.dataset.crossSellGroup;
            const compact = group.querySelector('[data-cross-sell-expand]');
            const panel = group.querySelector('[data-cross-sell-panel]');
            group.hidden = false;
            if (compact) {
              compact.hidden = false;
              compact.setAttribute('aria-expanded', 'false');
            }
            if (panel) panel.hidden = true;
            group.toggleAttribute('data-preferred', family === preferredFamily);
          });
        }

        expand(family, source = 'automatic') {
          if (!VALID_FAMILIES.has(family)) return;
          const target = this.querySelector(`[data-cross-sell-group="${family}"]`);
          if (!target) {
            this.showCompact();
            return;
          }

          this.querySelectorAll('[data-cross-sell-group]').forEach((group) => {
            const isTarget = group === target;
            const compact = group.querySelector('[data-cross-sell-expand]');
            const panel = group.querySelector('[data-cross-sell-panel]');
            group.hidden = !isTarget;
            if (compact) {
              compact.hidden = isTarget;
              compact.setAttribute('aria-expanded', String(isTarget));
            }
            if (panel) panel.hidden = !isTarget;
          });

          this.state.last_family = family;
          this.state.impressed[family] = true;
          this.state = writeState(this.state);
          analytics('cart_cross_sell_impression', this.eventData(family, 'full', { trigger: source }));
          if (source === 'manual') {
            analytics('cart_cross_sell_expand', this.eventData(family, 'full'));
          }
        }

        handleEligibleAdd(detail) {
          const family = detail?.family;
          if (!VALID_FAMILIES.has(family)) {
            this.showCompact(this.preferredFamily());
            return false;
          }

          this.state = readState(this.token);
          this.state.last_family = family;
          if (this.state.seen[family]) {
            this.state = writeState(this.state);
            this.showCompact(family);
            return false;
          }

          this.state.seen[family] = true;
          this.state = writeState(this.state);
          this.expand(family, 'add_to_cart');
          return true;
        }

        async addAccessory(button) {
          if (button.disabled || button.classList.contains('is-loading')) return;
          const item = button.closest('[data-cross-sell-item]');
          const variantId = item?.querySelector('[data-cross-sell-variant]')?.value;
          if (!variantId) return;

          button.classList.add('is-loading');
          button.disabled = true;
          const originalLabel = button.querySelector('span')?.textContent || 'Agregar';
          if (button.querySelector('span')) button.querySelector('span').textContent = 'Agregando…';

          try {
            const sectionIds = ['cart-drawer', 'cart-icon-bubble', 'cart-icon-bubble-sticky'];
            if (this.surface === 'page') sectionIds.push('main-cart-items');
            const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: Number(variantId), quantity: 1 }],
                sections: sectionIds,
                sections_url: window.location.pathname,
              }),
            });
            const cartState = await response.json();
            if (!response.ok || cartState.status) throw new Error(cartState.description || cartState.message || 'No se pudo agregar');

            analytics(
              'cart_cross_sell_add',
              this.eventData(button.dataset.family, 'full', {
                product_id: button.dataset.productId,
                product_handle: button.dataset.productHandle,
                variant_id: String(variantId),
              }),
            );

            if (this.surface === 'page') {
              window.location.reload();
              return;
            }

            const drawer = document.querySelector('cart-drawer');
            if (drawer?.renderContents) drawer.renderContents(cartState);
            if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-cross-sell',
                productVariantId: String(variantId),
                cartData: cartState,
              });
            }
          } catch (error) {
            console.error('INGENIO cross-sell:', error);
            if (button.querySelector('span')) button.querySelector('span').textContent = 'Intenta de nuevo';
            setTimeout(() => {
              const liveButton = this.querySelector(`[data-cross-sell-add][data-product-handle="${button.dataset.productHandle}"]`);
              if (liveButton?.querySelector('span')) liveButton.querySelector('span').textContent = originalLabel;
              if (liveButton) liveButton.disabled = false;
            }, 1800);
          } finally {
            button.classList.remove('is-loading');
          }
        }

      },
    );
  }

  document.addEventListener('ingenio:cart-add-rendered', (event) => {
    document.querySelectorAll('cart-cross-sell').forEach((crossSell) => {
      crossSell.handleEligibleAdd(event.detail || {});
    });
  });

  document.addEventListener(
    'click',
    (event) => {
      const bundleCheckout = event.target.closest(
        '#bundlesuiteContainer .side-cart-checkout-cta button',
      );
      if (
        !bundleCheckout ||
        bundleCheckout.classList.contains('requirements-not-met') ||
        bundleCheckout.disabled ||
        bundleCheckout.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      const itemCount = Number(
        document.querySelector('cart-drawer')?.dataset.cartItemCount || 0,
      );
      writePendingAdd('puzzle', itemCount);
    },
    true,
  );

  document.addEventListener('ingenio:cart-manual-open', () => {
    const crossSell = document.querySelector('cart-cross-sell[data-surface="drawer"]');
    crossSell?.showCompact(crossSell.preferredFamily());
  });

  document.addEventListener('ingenio:cart-close', () => {
    const crossSell = document.querySelector('cart-cross-sell[data-surface="drawer"]');
    crossSell?.showCompact(crossSell.preferredFamily());
  });

  document.addEventListener('ingenio:cart-drawer-rendered', (event) => {
    if (event.detail?.itemCount === 0) clearState();
  });

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('[name="checkout"], [href$="/checkout"]');
    if (!checkout) return;
    const crossSell = document.querySelector('cart-cross-sell[data-surface="page"]') || document.querySelector('cart-cross-sell');
    if (!crossSell) return;
    const families = Object.entries(crossSell.state?.impressed || {})
      .filter(([, impressed]) => impressed)
      .map(([family]) => family);
    families.forEach((family) => {
      analytics('cart_cross_sell_checkout_after_impression', crossSell.eventData(family, 'checkout'));
    });
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    document.querySelectorAll('cart-cross-sell').forEach((crossSell) => {
      crossSell.state = readState(crossSell.token);
      crossSell.showCompact(crossSell.preferredFamily());
    });
  });
})();
